/**
 * Pure aggregation helpers for the AR/AP pages. Every sum is `BigInt` over
 * the server's integer-Rial digit strings (AGENTS.md §8); nothing here
 * converts to `Number` except bounded presentation values (percentages,
 * day counts). Inputs are the rows of `GET .../finance/{ar|ap}/aging`, which
 * the API returns whole (no paging) and restricted to open/partial items —
 * see apps/api/src/finance/ar.controller.ts (`aging`) and
 * packages/modules/accounting/src/subledger/aging.ts (`queryAging`).
 */
import type { AgingBucket, AgingItem } from '../../lib/types/finance.js';

export interface PartyBalanceRow {
  readonly id: string;
  readonly partyId: string;
  readonly itemCount: number;
  readonly balanceRial: string;
  /** Sum of items whose due date is before the as-of date. */
  readonly overdueRial: string;
  /** Largest `daysPastDue` among overdue items (0 when none is overdue). */
  readonly maxDaysPastDue: number;
  /** Single control-account id when every item of the party shares one. */
  readonly controlAccountId: string | null;
}

export type PartyBalanceStatus = 'current' | 'overdue' | 'severe';

export function partyBalanceStatus(
  row: Pick<PartyBalanceRow, 'maxDaysPastDue'>,
): PartyBalanceStatus {
  if (row.maxDaysPastDue > 90) return 'severe';
  if (row.maxDaysPastDue > 0) return 'overdue';
  return 'current';
}

export function aggregateByParty(items: readonly AgingItem[]): PartyBalanceRow[] {
  interface Acc {
    itemCount: number;
    balance: bigint;
    overdue: bigint;
    maxDays: number;
    control: string | null | undefined;
  }
  const byParty = new Map<string, Acc>();
  for (const item of items) {
    const acc = byParty.get(item.partyId) ?? {
      itemCount: 0,
      balance: 0n,
      overdue: 0n,
      maxDays: 0,
      control: undefined,
    };
    const open = BigInt(item.openBalanceRial);
    acc.itemCount += 1;
    acc.balance += open;
    if (item.daysPastDue > 0) {
      acc.overdue += open;
      acc.maxDays = Math.max(acc.maxDays, item.daysPastDue);
    }
    acc.control =
      acc.control === undefined || acc.control === item.controlAccountId
        ? item.controlAccountId
        : null;
    byParty.set(item.partyId, acc);
  }
  return [...byParty.entries()]
    .map(([partyId, acc]) => ({
      id: partyId,
      partyId,
      itemCount: acc.itemCount,
      balanceRial: acc.balance.toString(),
      overdueRial: acc.overdue.toString(),
      maxDaysPastDue: acc.maxDays,
      controlAccountId: acc.control ?? null,
    }))
    .sort((a, b) => {
      const diff = BigInt(b.balanceRial) - BigInt(a.balanceRial);
      return diff > 0n ? 1 : diff < 0n ? -1 : 0;
    });
}

export function sumRial(values: readonly string[]): bigint {
  return values.reduce((sum, v) => sum + BigInt(v), 0n);
}

export interface AgingShare {
  readonly bucket: string;
  readonly itemCount: number;
  readonly openBalanceRial: string;
  /** Share of the grand total in tenths of a percent (0–1000), BigInt-derived. */
  readonly permille: number;
}

/** Per-bucket share of the grand total; `permille` is 0 for every bucket when the total is 0. */
export function agingShares(buckets: readonly AgingBucket[]): {
  readonly shares: readonly AgingShare[];
  readonly totalRial: string;
} {
  const total = sumRial(buckets.map((b) => b.openBalanceRial));
  const shares = buckets.map((b) => ({
    bucket: b.bucket,
    itemCount: b.itemCount,
    openBalanceRial: b.openBalanceRial,
    permille: total > 0n ? Number((BigInt(b.openBalanceRial) * 1000n) / total) : 0,
  }));
  return { shares, totalRial: total.toString() };
}

/** Whole or one-decimal Persian percent from tenths of a percent. */
export function formatPermille(permille: number): string {
  const whole = Math.floor(permille / 10);
  const tenth = permille % 10;
  const text = tenth === 0 ? String(whole) : `${whole}.${tenth}`;
  return `${text.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]!).replace('.', '٫')}٪`;
}

/** Severity colour per aging bucket (success → danger), via design tokens. */
export const AGING_BUCKET_COLOR: Record<string, string> = {
  current: 'var(--sk-success)',
  '1-30': 'var(--sk-info)',
  '31-60': 'var(--sk-warn)',
  '61-90': 'color-mix(in oklch, var(--sk-warn) 55%, var(--sk-danger))',
  '90+': 'var(--sk-danger)',
};
